import { useEffect, useState } from "react";
import { NxtbnDrawer } from "..";
import { useParams } from "react-router-dom";
import { useQuery, gql, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { NXForm } from "../common";
import InputField from "../common/InputField";

const GET_CATEGORY_AND_TRANSLATION = gql`
  query MyQuery($id: ID!, $langCode: String!) {
    category(id: $id) {
      dbId
      description
      metaDescription
      id
      metaTitle
      name
    }
    categoryTranslation(baseCategoryId: $id, langCode: $langCode) {
      dbId
      description
      id
      metaDescription
      metaTitle
      name
    }
  }
`;

const UPDATE_CATEGORY_TRANSLATION = gql`
  mutation updateCategoryTranslation(
    $baseCategoryId: Int!,
    $langCode: String!,
    $name: String,
    $description: String,
    $metaTitle: String,
    $metaDescription: String
  ) {
    updateCategoryTranslation(
      baseCategoryId: $baseCategoryId,
      langCode: $langCode,
      name: $name,
      description: $description,
      metaTitle: $metaTitle,
      metaDescription: $metaDescription
    ) {
      categoryTranslation {
        dbId
        name
        description
        metaTitle
        metaDescription
      }
    }
  }
`;

function CategoryTranslationForm({ isOpen, onClose, id }: { isOpen: boolean; onClose: () => void, id: any }) {
    const [formData, setFormData] = useState<any | null>(null);
    const [originalString, setOriginalString] = useState<any | null>(null);
    const { languageCode } = useParams<{ languageCode: string }>();

    const { loading, error, data, refetch } = useQuery(GET_CATEGORY_AND_TRANSLATION, {
        variables: { id, langCode: languageCode },
        skip: !id || !languageCode,
        notifyOnNetworkStatusChange: true,
    });

    const [updateCategoryTranslation, { loading: mutationLoading }] = useMutation(UPDATE_CATEGORY_TRANSLATION);

    useEffect(() => {
        if (isOpen) {
            refetch();
        }
    }, [isOpen, refetch]);

    useEffect(() => {
        if (data) {
            setOriginalString({
                description: data.category.description,
                metaDescription: data.category.metaDescription,
                metaTitle: data.category.metaTitle,
                name: data.category.name,
            });

            setFormData({
                description: data.categoryTranslation?.description,
                name: data.categoryTranslation?.name,
                metaTitle: data.categoryTranslation?.metaTitle,
                metaDescription: data.categoryTranslation?.metaDescription,
            });
        }
    }, [data, id, languageCode]);

    const handleChange = (e: any) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await updateCategoryTranslation({
                variables: {
                    baseCategoryId: id,
                    langCode: languageCode,
                    name: formData.name,
                    description: formData.description,
                    metaTitle: formData.metaTitle,
                    metaDescription: formData.metaDescription,
                },
            });
            toast.success('Category Translation Updated Successfully');
            onClose();
        } catch (err) {
            toast.error('Error updating translation');
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <NxtbnDrawer size="lg" title={`Category Translation Form`} isOpen={isOpen} onClose={onClose}>
            <NXForm onSubmit={handleSubmit} className="p-6">
                <div key={id} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Original Name</label>
                        <p className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-800">
                            {originalString?.name || "N/A"}
                        </p>
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Translated Name</label>
                        <InputField
                            type="text"
                            name="name"
                            value={formData?.name || ""}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Original Description</label>
                        <p className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-800">
                            {originalString?.description || "N/A"}
                        </p>
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Translated Description</label>
                        <InputField
                            type="text"
                            name="description"
                            value={formData?.description || ""}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Original Meta Title</label>
                        <p className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-800">
                            {originalString?.metaTitle || "N/A"}
                        </p>
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Translated Meta Title</label>
                        <InputField
                            type="text"
                            name="metaTitle"
                            value={formData?.metaTitle || ""}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Original Meta Description</label>
                        <p className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-800">
                            {originalString?.metaDescription || "N/A"}
                        </p>
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Translated Meta Description</label>
                        <InputField
                            type="text"
                            name="metaDescription"
                            value={formData?.metaDescription || ""}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                </div>
                <div className="mt-6 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="mr-3 inline-flex justify-center rounded-md border border-transparent px-4 py-2 bg-gray-600 text-base font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 bg-primary-600 text-base font-medium text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    >
                        Save
                    </button>
                </div>
            </NXForm>
        </NxtbnDrawer>
    );
}

export default CategoryTranslationForm;
